<template>
  <div class="max-w-96">
    <form class="form-wrapper" @submit.prevent="onSubmit">
      <InputWrapped
        name="email"
        :label="t('input.label.email')"
        :placeholder="t('input.label.email')"
        type="email"
        v-model="email"
        v-bind="emailAttrs"
        :error="errors.email"
        autocomplete="username"
        :disabled="isLoginFanFetching"
      />
      <InputWrapped
        name="password"
        :label="t('input.label.password')"
        :placeholder="t('input.label.password')"
        type="password"
        v-model="password"
        v-bind="passwordAttrs"
        :error="errors.password"
        autocomplete="current-password"
        :disabled="isLoginFanFetching"
      />
      <Button type="submit" :disabled="isLoginFanFetching">
        <Loader2 v-if="isLoginFanFetching" class="h-4 w-4 animate-spin stroke-white" />
        {{ isLoginFanFetching ? t('common.signingIn') : t('common.signIn') }}
      </Button>
      <InfoBar
        v-if="loginFanError || (loginMagicLinkError && !isMagicLinkToken)"
        variant="destructive"
        :title="t('common.error')"
        :message="loginFanError?.message || loginMagicLinkError?.message || t('common.tryAgain')"
        @close="
          loginFanError = null;
          loginMagicLinkError = null;
        "
      />
    </form>
    <div class="flex flex-wrap justify-center gap-2 pb-8 pt-4 text-sm text-primary lg:py-4">
      <RouterLink
        class="link text-shadow text-center underline underline-offset-2"
        :class="{ 'pointer-events-none opacity-50': isLoginFanFetching }"
        :to="{ name: 'MagicLink' }"
        :disabled="isLoginFanFetching"
      >
        {{ t('common.useMagicLinkInstead') }}
      </RouterLink>
      |
      <RouterLink
        class="link text-shadow text-center underline underline-offset-2"
        :class="{ 'pointer-events-none opacity-50': isLoginFanFetching }"
        :to="{ name: 'ForgotPassword' }"
        :disabled="isLoginFanFetching"
      >
        {{ t('common.forgotPassword') }}
      </RouterLink>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch, onBeforeUnmount } from 'vue';
import { useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/yup';
import * as yup from 'yup';
import { useAccountStore } from '@stores/account.store';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';

// Components
import InputWrapped from '@modules/InputWrapped/InputWrapped.vue';
import Button from '@ui/button/Button.vue';
import InfoBar from '@generics/InfoBar.vue';

// Icons
import { Loader2 } from 'lucide-vue-next';

const { t } = useI18n();

interface LoginForm {
  email: string;
  password: string;
}

const isMagicLinkToken = computed(() =>
  loginMagicLinkError.value?.message === 'incorrect role, wanted magicLink, got signup'
    ? true
    : false,
);

const accountStore = useAccountStore();
const { loginFanError, isLoginFanFetching, loginMagicLinkError } = storeToRefs(accountStore);

const schema = toTypedSchema(
  yup.object({
    email: yup.string().required(t('errors.required')).email(t('errors.invalidEmail')),
    password: yup.string().required(t('errors.required')),
  }),
);

const { errors, handleSubmit, defineField, values } = useForm<LoginForm>({
  validationSchema: schema,
  validateOnMount: false,
  initialValues: {
    email: accountStore.guestEmail || '',
    password: '',
  },
});
const [email, emailAttrs] = defineField('email', {
  validateOnModelUpdate: false,
  validateOnBlur: true,
});

watch(email, (newValue) => {
  if (newValue !== accountStore.guestEmail) {
    accountStore.guestEmail = newValue;
  }
});

const [password, passwordAttrs] = defineField('password', {
  validateOnModelUpdate: false,
  validateOnBlur: true,
});

const onSubmit = handleSubmit((values) => {
  accountStore.loginFan(values.email, values.password);
});

onBeforeUnmount(() => {
  password.value = '';
});

watch(
  values,
  () => {
    loginFanError.value = null;
    loginMagicLinkError.value = null;
  },
  { deep: true },
);
</script>
